import React, { useMemo } from 'react';
import { useOrganization } from '@clerk/clerk-react';
import { ACTIONS, AREA, MATRIX_PAGES } from 'modules/Common/config/permissionsMatrix';
import { OrganizationType } from 'modules/Common/utils/Utility';
import usePermissionsAdminStore from '../stores/permissionsAdminStore';
import { useTranslation } from 'react-i18next';

const buildCrudColumns = (t) => [
  { action: ACTIONS.read, label: t('admin:crud.read', 'Read') },
  { action: ACTIONS.create, label: t('admin:crud.create', 'Create') },
  { action: ACTIONS.edit, label: t('admin:crud.edit', 'Edit') },
  { action: ACTIONS.delete, label: t('admin:crud.delete', 'Delete') },
];

const buildAreaLabels = (t) => ({
  [AREA.Declarant]: t('admin:matrix.declarantArea', 'Declarant area'),
  [AREA.Operator]: t('admin:matrix.operatorArea', 'Operator area'),
});

/**
 * Group MATRIX_PAGES by area, then by parentLabel (or 'None' for top-level
 * entries). The grouping shape mirrors the User Matrix Excel — area → group →
 * resource — so the grid reads as the spec it implements.
 */
const useGroupedPages = () =>
  useMemo(() => {
    const byArea = new Map();
    for (const page of MATRIX_PAGES) {
      if (!byArea.has(page.area)) byArea.set(page.area, new Map());
      const groupKey = page.parentLabel || '__top__';
      const byGroup = byArea.get(page.area);
      if (!byGroup.has(groupKey)) byGroup.set(groupKey, []);
      byGroup.get(groupKey).push(page);
    }
    return byArea;
  }, []);

/**
 * The role permissions editor grid. Reads draft state + dirty flag from the
 * zustand store; the parent page owns fetching and saving.
 *
 * Props:
 *   - readOnly: disables every checkbox + row toggle (used for system roles).
 */
const RoleMatrixGrid = ({ readOnly = false }) => {
  const { t } = useTranslation('admin');
  const CRUD_COLUMNS = useMemo(() => buildCrudColumns(t), [t]);
  const AREA_LABEL = useMemo(() => buildAreaLabels(t), [t]);
  const grouped = useGroupedPages();
  const { organization } = useOrganization();
  const userType = organization?.publicMetadata?.type;
  const draft = usePermissionsAdminStore((s) => s.editorDraft) || {};
  const toggleGrant = usePermissionsAdminStore((s) => s.toggleGrant);
  const setResourceActions = usePermissionsAdminStore((s) => s.setResourceActions);

  // Hide the area that doesn't match the org's type — a declarant admin has
  // no use for the MFG section and vice versa. Permissions for the hidden
  // area are preserved in the draft (e.g. when "Copy grants from" pulls in
  // both areas from a system role); they're just not editable here.
  const visibleAreas = useMemo(() => {
    if (userType !== OrganizationType.Declarant && userType !== OrganizationType.Operator) {
      return grouped;
    }
    const next = new Map();
    for (const [area, byGroup] of grouped.entries()) {
      if (area === userType) next.set(area, byGroup);
    }
    return next;
  }, [grouped, userType]);

  // Draft keys are bare slugs (`resource:action`) — see initEditor in the
  // store. Backend-returned permissions are normalised to bare form on the
  // way in, so the matrix reads/writes consistently without the org: prefix.
  const isGranted = (resource, action) => Boolean(draft[`${resource}:${action}`]);
  const rowAll = (resource) => CRUD_COLUMNS.every(({ action }) => isGranted(resource, action));

  const handleToggleRow = (resource) => {
    if (readOnly) return;
    if (rowAll(resource)) {
      setResourceActions(resource, []);
    } else {
      setResourceActions(
        resource,
        CRUD_COLUMNS.map((c) => c.action),
      );
    }
  };

  return (
    <div className="space-y-8 w-full max-w-3xl mx-auto">
      {[...visibleAreas.entries()].map(([area, byGroup]) => (
        <section key={area}>
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-2">
            {AREA_LABEL[area] || area}
          </h2>
          {/* overflow-x-auto so narrow viewports (modal-hosted or sub-768px
              page width) get a horizontal scroll instead of pushing the
              row beyond the page edge. min-w on the table holds the
              columns at a readable size while scrolling. */}
          <div className="border border-slate-200 rounded-xl overflow-x-auto">
            <table className="min-w-[480px] w-full divide-y divide-slate-100">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    {t('table.resource', 'Resource')}
                  </th>
                  {CRUD_COLUMNS.map(({ label }) => (
                    <th
                      key={label}
                      className="w-20 px-2 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-slate-500"
                    >
                      {label}
                    </th>
                  ))}
                  <th className="w-24 px-3 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    {t('table.row', 'Row')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {[...byGroup.entries()].flatMap(([groupKey, pages]) => {
                  const showGroupHeader = groupKey !== '__top__';
                  const rows = [];
                  if (showGroupHeader) {
                    rows.push(
                      <tr
                        key={`${area}-${groupKey}-header`}
                        className="bg-slate-50/50"
                      >
                        <td
                          colSpan={CRUD_COLUMNS.length + 2}
                          className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500"
                        >
                          {groupKey}
                        </td>
                      </tr>,
                    );
                  }
                  for (const page of pages) {
                    rows.push(
                      <tr
                        key={`${area}-${page.resourceKey}`}
                        className="hover:bg-slate-50"
                      >
                        <td className="px-3 py-2 text-sm text-slate-700">{page.sidebarLabel}</td>
                        {CRUD_COLUMNS.map(({ action }) => {
                          const checked = isGranted(page.resourceKey, action);
                          return (
                            <td
                              key={action}
                              className="px-2 py-2 text-center"
                            >
                              {/* Visual is driven from React `checked` state
                                  via the wrapper div's className — not from
                                  the input's `:checked` CSS pseudo — so the
                                  primary colour fill is guaranteed to follow
                                  the store regardless of browser quirks. */}
                              <label
                                className={`inline-flex items-center justify-center ${
                                  readOnly ? 'cursor-not-allowed' : 'cursor-pointer'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  disabled={readOnly}
                                  checked={checked}
                                  onChange={() =>
                                    !readOnly && toggleGrant(page.resourceKey, action)
                                  }
                                  className="sr-only peer"
                                  data-testid={`grant-${page.resourceKey}-${action}`}
                                />
                                <span
                                  className={`w-4 h-4 rounded border flex items-center justify-center transition-colors peer-focus:ring-1 peer-focus:ring-primaryColor/40 ${
                                    checked
                                      ? 'bg-primaryColor border-primaryColor'
                                      : 'bg-white border-slate-300'
                                  } ${readOnly ? 'opacity-50' : ''}`}
                                >
                                  {checked && (
                                    <svg
                                      viewBox="0 0 16 16"
                                      className="w-3 h-3"
                                      fill="white"
                                      aria-hidden="true"
                                    >
                                      <path d="m13.854 3.646-7.5 7.5a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6 10.293l7.146-7.147a.5.5 0 0 1 .708.708z" />
                                    </svg>
                                  )}
                                </span>
                              </label>
                            </td>
                          );
                        })}
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            disabled={readOnly}
                            onClick={() => handleToggleRow(page.resourceKey)}
                            className="text-[11px] text-primaryColor hover:underline disabled:text-slate-400 disabled:no-underline"
                          >
                            {rowAll(page.resourceKey)
                              ? t('common:actions.clear', 'Clear')
                              : t('admin:matrix.all', 'All')}
                          </button>
                        </td>
                      </tr>,
                    );
                  }
                  return rows;
                })}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
};

export default RoleMatrixGrid;
